import React, { useState } from 'react';
import { cn } from 'cn';
import {
  HoverCard as UIHoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';

type HoverCardProps = {
  /** O que aparece dentro do card: texto rico, tabela, lista. Para texto curto, use o `Tooltip`. */
  content?: React.ReactNode;
  /** Cabeçalho em negrito, acima do conteúdo. */
  title?: React.ReactNode;
  /** Lado do gatilho em que o card abre. */
  placement?: 'top' | 'right' | 'bottom' | 'left';
  /** Alinhamento em relação ao gatilho, no eixo do lado escolhido. */
  align?: 'start' | 'center' | 'end';
  /** Distância do gatilho, em px. */
  sideOffset?: number;
  /** Onde o card é renderizado: o elemento ou o id dele. Use na tela cheia (ex.: Card de Metas), senão o card abre por trás. */
  container?: HTMLElement | string;
  /** Classes do conteúdo (ex.: limite de altura com rolagem). */
  className?: string;
  /** Aberto ou fechado, quando a tela controla o estado. */
  open?: boolean;
  /** Chamado ao abrir e ao fechar, com o novo estado. */
  onOpenChange?: (open: boolean) => void;
  /** O gatilho: um único elemento que aceite ref. */
  children: React.ReactElement;
};

/**
 * Card branco que abre ao passar o mouse (ou focar) no gatilho. Aceita o mouse dentro, então o conteúdo pode rolar ou ter links.
 * @import import HoverCard from 'src/componentes/hover-card';
 */
const HoverCard = ({
  content,
  title,
  placement = 'top',
  align,
  sideOffset,
  container,
  className,
  open,
  onOpenChange,
  children,
}: HoverCardProps) => {
  const [aberto, setAberto] = useState(false);

  const handleOpenChange = (value: boolean) => {
    setAberto(value);
    onOpenChange?.(value);
  };

  // container pode ser o id de um elemento que ainda não existe quando a tela
  // monta (ex.: o card de metas em tela cheia); por isso é buscado ao abrir.
  const portalContainer =
    typeof container === 'string'
      ? ((open ?? aberto) && document.getElementById(container)) || undefined
      : container;

  return (
    <UIHoverCard
      openDelay={100}
      closeDelay={150}
      open={open}
      onOpenChange={handleOpenChange}
    >
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        side={placement}
        align={align}
        sideOffset={sideOffset}
        container={portalContainer}
        className={cn('tw:w-auto', className)}
      >
        {title && <div className="tw:mb-2 tw:font-semibold">{title}</div>}
        {content}
      </HoverCardContent>
    </UIHoverCard>
  );
};

export default HoverCard;
